import { useEffect, useMemo, useState } from 'react';
import { useIntegrationApp } from '@membranehq/react';
import { Option, SelectBox } from 'src/components/SelectBox';
import { Loading } from 'src/components/Loading';
import {
  DataSourceKey,
  listSegments,
} from 'src/services/integration-app.service';
import { useNotification } from 'src/contexts/NotificationContext';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useCampaignContext } from '../../contexts/CampaignContext';
import { useExtraStepConfiguration } from '../FieldMapping/ExtraStepConfiguration';

const ALL_CONTACTS_VALUE = '$fluents:all-contacts';

const ALL_CONTACTS_OPTION: Option = {
  value: ALL_CONTACTS_VALUE,
  label: 'All contacts',
};

export const ContactSegmentSelector = () => {
  const [segments, setSegments] = useState<Option[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const integrationApp = useIntegrationApp();
  const notification = useNotification();

  const { integratedCrm, segmentId, setSegmentId, syncInProgress } =
    useCrmSync();
  const { isInitializing } = useCampaignContext();
  const { setIsConfigurationComplete } = useExtraStepConfiguration();
  const disabled = syncInProgress || isInitializing;

  useEffect(() => {
    if (!integratedCrm?.value) {
      setSegments([]);
      return;
    }

    const loadSegments = async () => {
      setLoadingSegments(true);
      try {
        const result = await listSegments({
          client: integrationApp,
          integrationKey: integratedCrm.value,
          dataSourceKey: DataSourceKey.CONTACTS,
        });

        const segmentOptions: Option[] = result.records.map((segment) => ({
          value: segment.id,
          label: segment.name,
        }));

        setSegments(segmentOptions);
      } catch (error) {
        console.error('Failed to load CRM segments:', error);
        setSegments([]);
        notification.error('Failed to load CRM segments.');
      } finally {
        setLoadingSegments(false);
      }
    };

    loadSegments();
  }, [integrationApp, integratedCrm?.value, notification]);

  useEffect(() => {
    if (!integratedCrm?.value) {
      return;
    }

    setSegmentId(undefined);
  }, [integratedCrm?.value, setSegmentId]);

  useEffect(() => {
    setIsConfigurationComplete(true);
  }, [setIsConfigurationComplete]);

  const options = useMemo(() => {
    return [ALL_CONTACTS_OPTION, ...segments];
  }, [segments]);

  const selectedSegmentOption = useMemo(() => {
    if (!segmentId) {
      return ALL_CONTACTS_OPTION;
    }

    return (
      segments.find((segment) => segment.value === segmentId) ?? {
        value: segmentId,
        label: segmentId,
      }
    );
  }, [segmentId, segments]);

  const handleSegmentChange = (value: string) => {
    if (value === ALL_CONTACTS_VALUE) {
      setSegmentId(undefined);
      return;
    }

    setSegmentId(value);
  };

  return (
    <div className="m-6 bg-neutral-0 border border-neutral-200 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Select a Segment</h3>
        <div className="text-sm text-neutral-600 mb-4">
          Choose a HubSpot segment to import contacts from, or select all
          contacts.
        </div>

        {loadingSegments ? (
          <div className="flex items-center gap-2 text-neutral-600">
            <Loading size="6" />
            <div className="text-sm">Loading segments...</div>
          </div>
        ) : (
          <>
            <SelectBox
              options={options}
              value={selectedSegmentOption}
              onChange={handleSegmentChange}
              variant="outlined"
              size="medium"
              disabled={disabled}
            />

            {segments.length === 0 && (
              <div className="text-sm text-neutral-600 mt-2">
                No segments found. You can still import all contacts.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
